import { useResponsive } from 'ahooks';
import { Descriptions, DescriptionsProps, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { BookingStatus, ResourceBooking } from '@/models';
import {
  DATETIME_WITH_DAY_FORMAT,
  displayAddress,
  displayEmptyString,
  generateMapUrl,
} from '@/utils';

import { AppDetailsContentProps } from '.';

const { Link } = Typography;

export function AppOnsiteDetailsContent({ jobBooking, isJob }: AppDetailsContentProps) {
  const { t } = useTranslation();
  const breakpoint = useResponsive();
  const isVerified = jobBooking.status === BookingStatus.VERIFIED;
  const inHours = Number(
    (((jobBooking as ResourceBooking)?.verification?.value ?? 0) / 60).toFixed(2)
  );

  const items: DescriptionsProps['items'] = [
    {
      key: 'reference',
      label: t('common:reference'),
      children: t(jobBooking.reference),
    },
    {
      key: 'type',
      label: t('common:bookingType'),
      children: t(jobBooking.type),
    },
    {
      key: 'language',
      label: t('common:language'),
      children: jobBooking.language,
    },
    ...(jobBooking.alternativeLanguage
      ? [
          {
            key: 'alternativeLanguage',
            label: t('common:alternativeLanguage'),
            children: jobBooking.alternativeLanguage,
          },
        ]
      : []),
    {
      key: 'date',
      label: t('forms:startTime'),
      children: dayjs(jobBooking.date).format(DATETIME_WITH_DAY_FORMAT),
    },
    {
      key: 'duration',
      label: t('forms:duration'),
      children: (
        <>
          {t('minuteWithCount', {
            count: jobBooking.onsite?.duration,
          })}
          &nbsp; (
          {t('hourWithCount', {
            count: Number(((jobBooking.onsite?.duration ?? 0) / 60).toFixed(2)),
          })}
          )
        </>
      ),
    },
    {
      key: 'location',
      label: t('forms:location'),
      children: jobBooking?.onsite?.jobSite ? (
        <Link
          href={generateMapUrl({
            street: jobBooking.onsite.jobSite.street,
            zip: jobBooking.onsite.jobSite.zip,
            city: jobBooking.onsite.jobSite.city,
          })}
          target='_blank'
          rel='noreferrer'
        >
          {jobBooking.onsite?.jobSite.name} - {displayAddress(jobBooking.onsite?.jobSite)}
        </Link>
      ) : null,
    },
    {
      key: 'notes',
      label: t('forms:topic'),
      children: displayEmptyString(jobBooking.notes ?? '', t('common:noData')),
    },
    ...(jobBooking.gender
      ? [
          {
            key: 'gender',
            label: t('common:gender'),
            children: t(`common:${jobBooking.gender.toString().toLowerCase()}`),
          },
        ]
      : []),
    {
      key: 'contactPerson',
      label: t('common:contactPerson'),
      children: jobBooking.contactPerson.name,
    },
    ...(!isJob
      ? [
          {
            key: 'customer',
            label: t('common:customer'),
            children: (jobBooking as ResourceBooking).customer,
          },
        ]
      : []),
    ...(isVerified
      ? [
          {
            key: 'verified',
            label: t('common:VERIFIED'),
            children: (
              <>
                <strong>
                  {t('minuteWithCount', {
                    count: (jobBooking as ResourceBooking).verification?.value,
                  })}
                  &nbsp; (
                  {t('hourWithCount', {
                    count: inHours,
                  })}
                  )
                </strong>
                <br />
                {t('forms:startTime')}:&nbsp;
                {(jobBooking as ResourceBooking).verification?.startTime?.format(
                  DATETIME_WITH_DAY_FORMAT
                )}
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <Descriptions
      size='small'
      bordered
      layout={breakpoint.lg ? 'horizontal' : 'vertical'}
      items={items}
      labelStyle={{ fontWeight: 'bolder' }}
      column={1}
    />
  );
}
